/**
 * Tester onboarding: preset profiles (Imri / Carlos), one click each.
 * Handles the modal, the top-right chip, and localStorage persistence.
 */
import { type Locale } from "./ui.strings";
import { fullName, initialsOf, loadProfile, saveProfile, type UserProfile } from "./user";

const byId = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

/* Preset testers — one click fills the whole profile */
const PRESET_USERS: Record<string, { firstName: string; phone: string }> = {
  imri: { firstName: "Imri", phone: "0789868901" },
  carlos: { firstName: "Carlos", phone: "0650045766" },
};

export type Onboarding = {
  current: () => UserProfile | null;
  open: () => void;
};

export const setupOnboarding = ({ locale }: { locale: Locale }): Onboarding => {
  let profile = loadProfile();

  // Profile default language applied when landing without an explicit slug
  if (profile && window.location.pathname === "/" && profile.locale === "es") {
    window.location.replace("/es");
  }

  const overlay = byId<HTMLDivElement>("setup-overlay");

  const renderChip = () => {
    byId<HTMLSpanElement>("user-avatar").textContent = profile ? initialsOf({ profile }) : "?";
    byId<HTMLSpanElement>("user-name").textContent = profile ? fullName({ profile }) : "—";
  };

  const open = () => {
    for (const person of Object.keys(PRESET_USERS)) {
      byId<HTMLButtonElement>(`person-${person}`).dataset.active = String(
        profile?.firstName.toLowerCase() === person,
      );
    }
    byId<HTMLSelectElement>("setup-locale").value = profile?.locale ?? "es";
    overlay.hidden = false;
  };

  const applyPerson = ({ person }: { person: string }) => {
    const preset = PRESET_USERS[person];
    if (!preset) return;
    const next: UserProfile = {
      firstName: preset.firstName,
      lastName: "",
      phone: preset.phone,
      locale: byId<HTMLSelectElement>("setup-locale").value === "fr" ? "fr" : "es",
    };
    saveProfile({ profile: next });
    profile = next;
    overlay.hidden = true;
    renderChip();
    if (next.locale !== locale) window.location.href = `/${next.locale}`;
  };

  for (const person of Object.keys(PRESET_USERS)) {
    byId<HTMLButtonElement>(`person-${person}`).addEventListener("click", () =>
      applyPerson({ person }),
    );
  }
  byId<HTMLSpanElement>("user-chip").addEventListener("click", open);

  renderChip();
  if (!profile) open();

  return { current: () => profile, open };
};
